import arrayFrom from 'array-from';

/**
 * SimpleFilter
 *
 * Handles a "chip" based filter UI (desktop + modal mobile chips).
 * - Toggles active state of chips
 * - Keeps URL query params in sync (?filter=value&filter=value2...)
 * - Restores state from URL or (optional) sessionStorage
 * - Updates the filter receipt (#filterQuittung: #articleCounter + #selectedFilter)
 * - Opens/closes the filter modal (if present in markup)
 */
class SimpleFilter {
  /**
   * @param {Object} options
   * @param {HTMLElement} options.element - Module root element (provided by module loader)
   */
  constructor(options = {}) {
    this.element = options.element;

    // Filter markup lives outside of this controller element in the given page markup
    this.filterRoot = document.querySelector('#simple-filter');

    this.ACTIVE_CLASS = 'isActive';
    this.ALL_DESKTOP_ID = 'select-all-isDesktop';
    this.ALL_MOBILE_ID = 'select-all-isMobile';

    /**
     * SessionStorage key for persisted filter labels.
     * @type {string}
     */
    this.FILTER_STATE_LABEL = 'filterState';

    this.articleCounterEl = null;
    this.selectedFilterEl = null;

    this.articlesLabelSingular = 'Beitrag';
    this.articlesLabelPlural = 'Beiträge';
    this.allTopicsLabel = 'in allen Themen';

    /**
     * If true, the active filter labels are saved to sessionStorage.
     * Driven by data-use-storage="true|false" on the filter root element.
     * @type {boolean}
     */
    this.useStorage = false;

    // Modal elements (optional, depending on markup)
    this.modal = null;
    this.openModalBtn = null;
    this.closeModalBtn = null;
    this.confirmModalBtn = null;
  }

  /**
   * Initializes the component:
   * - reads config from DOM dataset
   * - wires modal events (if modal exists)
   * - wires chip events
   * - restores initial state (URL > sessionStorage > default)
   * - updates URL + receipt and optionally persists
   *
   * @returns {void}
   */
  init() {
    if (!this.filterRoot) {
      return;
    }

    this.articleCounterEl = this.filterRoot.querySelector('#articleCounter');
    this.selectedFilterEl = this.filterRoot.querySelector('#selectedFilter');

    this.articlesLabelSingular = this.filterRoot.dataset.articlesLabelSingular || this.articlesLabelSingular;
    this.articlesLabelPlural = this.filterRoot.dataset.articlesLabelPlural || this.articlesLabelPlural;
    this.allTopicsLabel = this.filterRoot.dataset.allTopicsLabel || this.allTopicsLabel;

    this.useStorage = String(this.filterRoot.dataset.useStorage) === 'true';

    this.cacheModalElements();
    this.bindModalEvents();

    this.bindChipEvents();
    this.applyInitialStateFromUrlOrStorageOrDefault();
    this.syncUiAndPersist();
  }

  /**
   * Caches modal-related DOM nodes (if they exist in the current markup).
   * @returns {void}
   */
  cacheModalElements() {
    this.modal = this.filterRoot.querySelector('.filter-modal');
    this.openModalBtn = this.filterRoot.querySelector('.filter-modal__btn.filter-modal__opener-btn');
    this.closeModalBtn = this.filterRoot.querySelector('.filter-modal .close-btn');
    this.confirmModalBtn = this.filterRoot.querySelector('.filter-modal__btn.filter-modal__confirm-btn');
  }

  /**
   * Binds modal open/close events:
   * - opener button click/keyboard
   * - close / confirm buttons
   * - ESC key
   *
   * @returns {void}
   */
  bindModalEvents() {
    if (!this.modal || !this.openModalBtn) {
      return;
    }

    this.openModalBtn.addEventListener('click', () => {
      this.openModal();
    });

    this.openModalBtn.addEventListener('keydown', (event) => {
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        this.openModal();
      }
    });

    if (this.closeModalBtn) {
      this.closeModalBtn.addEventListener('click', () => {
        this.closeModal();
      });
    }

    if (this.confirmModalBtn) {
      this.confirmModalBtn.addEventListener('click', () => {
        this.closeModal();
      });
    }

    document.addEventListener('keydown', (event) => {
      if (!this.isModalOpen()) {
        return;
      }

      if (event.key === 'Escape' || event.keyCode === 27) {
        event.preventDefault();
        this.closeModal();
      }
    });
  }

  /**
   * Opens the modal (removes "hide-element") and prevents body scrolling.
   * @returns {void}
   */
  openModal() {
    if (!this.modal) {
      return;
    }

    this.modal.classList.remove('hide-element');
    document.body.classList.add('no-scroll');
  }

  /**
   * Closes the modal (adds "hide-element"), enables body scrolling again,
   * and returns focus to the opener button (for accessibility).
   *
   * @returns {void}
   */
  closeModal() {
    if (!this.modal) {
      return;
    }

    this.modal.classList.add('hide-element');
    document.body.classList.remove('no-scroll');

    if (this.openModalBtn) {
      window.setTimeout(() => {
        this.openModalBtn.focus();
      }, 0);
    }
  }

  /**
   * @returns {boolean} True if modal exists and is currently visible.
   */
  isModalOpen() {
    return !!(this.modal && !this.modal.classList.contains('hide-element'));
  }

  /**
   * Binds click + keyboard handlers to all chips.
   * @returns {void}
   */
  bindChipEvents() {
    const chips = this.getAllChips();

    chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        this.onChipClick(chip);
      });

      chip.addEventListener('keydown', (event) => {
        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault();
          this.onChipClick(chip);
        }
      });
    });
  }

  /**
   * Handles chip toggle logic and triggers UI sync:
   * - toggles the clicked label (desktop + mobile twins)
   * - if all individual chips are selected -> switch to "All"
   * - if none selected -> switch to "All"
   *
   * @param {HTMLElement} chip
   * @returns {void}
   */
  onChipClick(chip) {
    const isAllChip = this.isAllChip(chip);

    if (isAllChip) {
      this.activateAll();
      this.syncUiAndPersist();
      return;
    }

    // Toggle this label on all corresponding chips (desktop + mobile)
    const label = this.getChipLabel(chip);
    if (label) {
      const currentlyActive = this.isChipLabelActive(label);
      this.setChipLabelActive(label, !currentlyActive);
    }

    // If all individual filters are selected OR none is selected -> activate "All filters"
    const allSelected = this.areAllIndividualFiltersSelected();
    const noneSelected = this.getActiveIndividualLabels().length === 0;
    // do not auto-activate "All" when there is only one individual filter
    const shouldAutoActivateAllOnAllSelected = this.getUniqueIndividualLabels().length > 1;

    if ((allSelected && shouldAutoActivateAllOnAllSelected) || noneSelected) {
      this.activateAll();
    } else {
      this.deactivateAllChip();
    }

    this.syncUiAndPersist();
  }

  /**
   * Restores active filters by priority:
   * 1) URL query params (?filter=...)
   * 2) sessionStorage (if enabled)
   * 3) default state (activate "All")
   *
   * @returns {void}
   */
  applyInitialStateFromUrlOrStorageOrDefault() {
    const labelsFromUrl = this.getLabelsFromUrl();

    if (labelsFromUrl.length > 0) {
      this.activateOnlyTheseLabels(labelsFromUrl);
      return;
    }

    if (this.useStorage) {
      const labelsFromStorage = this.getLabelsFromSessionStorage();
      if (labelsFromStorage.length > 0) {
        this.activateOnlyTheseLabels(labelsFromStorage);
        return;
      }
    }

    this.activateAll();
  }

  /**
   * Synchronizes side effects after state change:
   * - update URL query params
   * - update receipt (#filterQuittung)
   * - persist to sessionStorage if enabled
   *
   * @returns {void}
   */
  syncUiAndPersist() {
    this.updateUrlFromActiveFilters();
    this.updateQuittungCountAndText();
    this.saveToSessionStorageIfEnabled();
  }

  // -----------------------------
  // State getters
  // -----------------------------

  /**
   * Returns all chip elements (desktop + modal).
   * @returns {HTMLElement[]}
   */
  getAllChips() {
    // In the markup there are desktop chips in `.simple-filter` and mobile chips in the modal.
    // Both use the `.category-filter` class.
    const chips = document.querySelectorAll('.category-filter');
    return arrayFrom(chips);
  }

  /**
   * Returns all chips excluding the "All filters" chips.
   * @returns {HTMLElement[]}
   */
  getAllIndividualChips() {
    return this.getAllChips().filter(chip => !this.isAllChip(chip));
  }

  /**
   * @param {HTMLElement} chip
   * @returns {boolean} True if chip is one of the "All filters" chips.
   */
  isAllChip(chip) {
    const id = (chip.dataset && chip.dataset.id) ? chip.dataset.id : chip.id;
    return id === this.ALL_DESKTOP_ID || id === this.ALL_MOBILE_ID;
  }

  /**
   * @param {HTMLElement} chip
   * @returns {string|null} Filter label.
   */
  getChipLabel(chip) {
    return chip && chip.dataset ? chip.dataset.label : null;
  }

  /**
   * Parses chip count from data-count attribute.
   * @param {HTMLElement|null} chip
   * @returns {number}
   */
  getChipCount(chip) {
    const raw = chip && chip.dataset ? chip.dataset.count : null;
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  /**
   * Returns unique individual labels (dedupes desktop/mobile duplicates).
   * @returns {string[]}
   */
  getUniqueIndividualLabels() {
    const labels = new Set();
    this.getAllIndividualChips().forEach((chip) => {
      const label = this.getChipLabel(chip);
      if (label) {
        labels.add(label);
      }
    });
    return Array.from(labels);
  }

  /**
   * @param {string} label
   * @returns {HTMLElement[]}
   */
  getChipsByLabel(label) {
    return this.getAllChips().filter(chip => this.getChipLabel(chip) === label);
  }

  /**
   * @param {string} label
   * @returns {boolean}
   */
  isChipLabelActive(label) {
    const chips = this.getChipsByLabel(label);
    return chips.some(chip => chip.classList.contains(this.ACTIVE_CLASS));
  }

  /**
   * @returns {string[]} Active labels (individual filters only).
   */
  getActiveIndividualLabels() {
    const labels = this.getUniqueIndividualLabels();
    return labels.filter(label => this.isChipLabelActive(label));
  }

  /**
   * @returns {boolean} True if all individual filter labels are currently active.
   */
  areAllIndividualFiltersSelected() {
    const allLabels = this.getUniqueIndividualLabels();
    const activeLabels = this.getActiveIndividualLabels();
    return allLabels.length > 0 && activeLabels.length === allLabels.length;
  }

  // -----------------------------
  // State setters
  // -----------------------------

  /**
   * Activates/deactivates all chips that share the same label (desktop + mobile).
   * @param {string} label
   * @param {boolean} isActive
   * @returns {void}
   */
  setChipLabelActive(label, isActive) {
    const chips = this.getChipsByLabel(label);
    chips.forEach((chip) => {
      if (isActive) {
        chip.classList.add(this.ACTIVE_CLASS);
      } else {
        chip.classList.remove(this.ACTIVE_CLASS);
      }
    });
  }

  /**
   * Sets the UI to exactly the given labels (and adjusts "All" chip accordingly).
   * @param {string[]} labelsToActivate
   * @returns {void}
   */
  activateOnlyTheseLabels(labelsToActivate) {
    const normalized = new Set(labelsToActivate.map(v => String(v)));

    // deactivate all chips first
    this.getAllChips().forEach(chip => chip.classList.remove(this.ACTIVE_CLASS));

    // activate matching labels
    normalized.forEach((label) => {
      this.setChipLabelActive(label, true);
    });

    // handle "all" chip depending on completeness
    const allSelected = this.areAllIndividualFiltersSelected();
    const noneSelected = this.getActiveIndividualLabels().length === 0;
    // do not auto-activate "All" when there is only one individual filter
    const shouldAutoActivateAllOnAllSelected = this.getUniqueIndividualLabels().length > 1;

    if ((allSelected && shouldAutoActivateAllOnAllSelected) || noneSelected) {
      this.activateAll();
    } else {
      this.deactivateAllChip();
    }
  }

  /**
   * Activates "All filters" (and deactivates all individual filters).
   * @returns {void}
   */
  activateAll() {
    // "All filters" means: no individual filters active, only select-all active
    this.getAllIndividualChips().forEach(chip => chip.classList.remove(this.ACTIVE_CLASS));

    this.getAllChips().forEach((chip) => {
      if (this.isAllChip(chip)) {
        chip.classList.add(this.ACTIVE_CLASS);
      }
    });
  }

  /**
   * Deactivates the "All filters" chip(s) only.
   * @returns {void}
   */
  deactivateAllChip() {
    this.getAllChips().forEach((chip) => {
      if (this.isAllChip(chip)) {
        chip.classList.remove(this.ACTIVE_CLASS);
      }
    });
  }

  // -----------------------------
  // URL handling
  // -----------------------------

  /**
   * Reads active filter labels from URL query params.
   * @returns {string[]}
   */
  getLabelsFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const values = params.getAll('filter');
    return values
      .map(v => String(v).trim())
      .filter(v => v.length > 0);
  }

  /**
   * Writes the active filter labels to the URL query params (?filter=...).
   * @returns {void}
   */
  updateUrlFromActiveFilters() {
    const activeLabels = this.getActiveIndividualLabels();

    const params = new URLSearchParams(window.location.search);
    params.delete('filter');

    activeLabels.forEach((label) => {
      params.append('filter', label);
    });

    const newQuery = params.toString();
    const newUrl = newQuery ? `${window.location.pathname}?${newQuery}` : window.location.pathname;
    window.history.replaceState(null, '', newUrl);
  }

  // -----------------------------
  // Quittung (count + text)
  // -----------------------------

  /**
   * Updates:
   * - #articleCounter based on the counts of the active labels
   * - #selectedFilter ("in allen Themen" or "in A, B, C")
   *
   * @returns {void}
   */
  updateQuittungCountAndText() {
    if (!this.articleCounterEl || !this.selectedFilterEl) {
      return;
    }

    const activeLabels = this.getActiveIndividualLabels();
    const allMode = activeLabels.length === 0;

    // Count logic based purely on the chip counts:
    // - allMode: sum of all unique labels
    // - filterMode: sum of active unique labels
    const labelsToCount = allMode ? this.getUniqueIndividualLabels() : activeLabels;

    let count = 0;
    labelsToCount.forEach((label) => {
      // take count from first chip with that label (desktop/mobile duplicates exist)
      const chips = this.getChipsByLabel(label);
      const chip = chips.length > 0 ? chips[0] : null;
      count += this.getChipCount(chip);
    });

    const labelText = (count === 1) ? this.articlesLabelSingular : this.articlesLabelPlural;
    this.articleCounterEl.innerHTML = `${count} ${labelText}`;

    if (allMode) {
      this.selectedFilterEl.innerHTML = this.allTopicsLabel;
    } else {
      this.selectedFilterEl.innerHTML = `in ${activeLabels.join(', ')}`;
    }

    // all-topics count should ALWAYS be the total across ALL categories
    const allLabels = this.getUniqueIndividualLabels();
    let totalAllCount = 0;

    allLabels.forEach((label) => {
      const chips = this.getChipsByLabel(label);
      const chip = chips.length > 0 ? chips[0] : null;
      totalAllCount += this.getChipCount(chip);
    });

    this.updateAllTopicsChipCount(totalAllCount);
  }

  updateAllTopicsChipCount(count) {
    const safeCount = Number.isFinite(count) ? count : 0;

    this.getAllChips().forEach((chip) => {
      if (!this.isAllChip(chip)) {
        return;
      }

      /* eslint-disable-next-line no-param-reassign */
      chip.dataset.count = String(safeCount);

      const numberEl = chip.querySelector('.category-filter__number');
      if (numberEl) {
        numberEl.textContent = String(safeCount);
      }
    });
  }

  // -----------------------------
  // SessionStorage
  // -----------------------------

  /**
   * Persists the current active filter labels to sessionStorage (if enabled).
   * @returns {void}
   */
  saveToSessionStorageIfEnabled() {
    if (!this.useStorage) {
      return;
    }

    const activeLabels = this.getActiveIndividualLabels();
    // saving only labels keeps it simple and independent of the chip ids
    sessionStorage.setItem(this.FILTER_STATE_LABEL, JSON.stringify(activeLabels));
  }

  /**
   * Reads persisted filter labels from sessionStorage.
   * @returns {string[]}
   */
  getLabelsFromSessionStorage() {
    const raw = sessionStorage.getItem(this.FILTER_STATE_LABEL);
    if (!raw) {
      return [];
    }

    try {
      const parsed = JSON.parse(raw);

      if (Array.isArray(parsed)) {
        return parsed.map(v => String(v));
      }

      return [];
    } catch (error) {
      return [];
    }
  }
}

export default SimpleFilter;
